// Client-side geo gate.
//
// Calls ipapi.co once per session (cached in sessionStorage for 24h) and
// reports whether the visitor's IP resolves to Gujarat, India. The router
// uses this to redirect blocked visitors to /blocked before any panel or
// login page renders.
//
// Caveats:
//   - This is a *frontend* check. A determined user can bypass with a VPN
//     or by editing localStorage. It's a soft compliance gate, not security.
//   - Fail-open: if the geo-IP API is down/unreachable, we DO NOT block.
//     Locking everyone out due to a third-party outage is worse than the
//     occasional miss.

// Bump the version suffix any time the provider or matching rules change so
// stale verdicts from old code don't get reused.
const STORAGE_KEY = 'ipu_geo_check_v2';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

function readCache() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writeCache(blocked) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ts: Date.now(), blocked: !!blocked })
    );
  } catch (e) {
    // sessionStorage can throw in private/strict modes — non-fatal.
  }
}

// Geo-IP providers in priority order. Each entry maps the raw JSON shape to a
// normalized { country, region } pair so the matching logic stays in one
// place. We try them in order until one returns a usable answer; that way a
// single provider being down / rate-limiting (HTTP 429) doesn't silently
// drop us to fail-open.
const PROVIDERS = [
  {
    name: 'ipinfo.io',
    url: 'https://ipinfo.io/json',
    parse: (d) => ({
      country: String(d.country || '').toUpperCase().trim(),
      region: String(d.region || '').toLowerCase().trim(),
    }),
  },
  {
    name: 'ipapi.co',
    url: 'https://ipapi.co/json/',
    parse: (d) => ({
      country: String(d.country_code || '').toUpperCase().trim(),
      region: String(d.region || '').toLowerCase().trim(),
      regionCode: String(d.region_code || '').toUpperCase().trim(),
    }),
  },
];

function matchesBlocked({ country, region, regionCode }) {
  if (country !== 'IN') return false;
  if (region === 'gujarat') return true;
  if (regionCode === 'GJ') return true;
  return false;
}

export async function isBlockedRegion() {
  const cached = readCache();
  if (cached) return cached.blocked;

  for (const p of PROVIDERS) {
    try {
      const res = await fetch(p.url, { headers: { Accept: 'application/json' } });
      if (!res.ok) continue; // try next provider on 4xx/5xx (e.g. 429 rate limit)
      const data = await res.json();
      const norm = p.parse(data);
      // Helpful in DevTools when tuning the match rules.
      // eslint-disable-next-line no-console
      console.info('[geoBlock]', p.name, norm);
      const blocked = matchesBlocked(norm);
      writeCache(blocked);
      return blocked;
    } catch (e) {
      // Network / DNS / CORS error — try the next provider.
      continue;
    }
  }

  // Every provider failed: fail-open. Don't write a cache entry so the next
  // navigation gets a fresh shot at the providers instead of being stuck on
  // a stale "not blocked" verdict.
  return false;
}

// Routes that stay reachable even when the visitor is in a blocked region.
// These are the customer-facing payment / withdraw / status pages that end
// users (not operators) hit. Everything else (panel + login) is blocked.
const PUBLIC_PREFIXES = [
  '/pay/',
  '/instant-withdraw',
  '/deposit/',
  '/create-payin/',
  '/create-payout/',
  '/success',
  '/pending',
  '/failed',
];

export function isPublicRoute(path) {
  if (!path) return false;
  return PUBLIC_PREFIXES.some(
    (p) => path === p || path === p.replace(/\/$/, '') || path.startsWith(p)
  );
}

// Pull the path out of a hash-routed URL: "#/login?foo=1" -> "/login".
// Defaults to "/" when there's no hash (root visit).
export function currentHashPath() {
  const raw = (typeof window !== 'undefined' && window.location.hash) || '';
  const stripped = raw.replace(/^#/, '') || '/';
  const qIdx = stripped.indexOf('?');
  return qIdx === -1 ? stripped : stripped.slice(0, qIdx);
}

// Replaces the entire document with an nginx-style 400 page and prevents the
// app from mounting. Called from main.js when the geo check trips.
export function renderHardBlock() {
  try {
    document.title = '400 Bad Request';
    document.documentElement.innerHTML =
      '<head><title>400 Bad Request</title></head>' +
      '<body style="margin:0;padding:0;background:#fff;color:#000;font-family:Times,serif;">' +
      '<center><h1>400 Bad Request</h1></center>' +
      '<hr><center>nginx</center>' +
      '</body>';
    // Stop any further script-driven work on this document.
    if (typeof window !== 'undefined' && window.stop) window.stop();
  } catch (e) {
    // ignore — we've done what we can
  }
}
